import React from "react";

/** Order matches dropdown / grouping; labels must match saved `category` on each item. */
export const EQUIPMENT_OPTIONAL_CATEGORIES = [
    "Sonstiges",
    "Aufnahme & Mikrofone",
    "Lautsprecher & Kopfhörer (Monitoring)",
    "Recorder, Software & Computer",
    "Mischpulte & Steuerung",
    "Klangbearbeitung (Preamps & Effekte)",
    "Instrumente",
    "Akustik & Kabel",
    "Lichtequipment",
    "Kameras und Objektive",
    "Stative",
    "Set-Accessoires",
];

const PHOTO_VIDEO_OPTIONAL_CATEGORIES = [
    "Lichtequipment",
    "Kameras und Objektive",
    "Stative",
    "Set-Accessoires",
    "Sonstiges",
];

const MUSIK_OPTIONAL_CATEGORIES = [
    "Aufnahme & Mikrofone",
    "Lautsprecher & Kopfhörer (Monitoring)",
    "Recorder, Software & Computer",
    "Mischpulte & Steuerung",
    "Klangbearbeitung (Preamps & Effekte)",
    "Instrumente",
    "Akustik & Kabel",
    "Sonstiges",
];

/** Optional paid-equipment categories allowed for the given studio types (same rules as StudioEquipment). */
export const getOptionalEquipmentCategories = (studioTypes) => {
    const types = Array.isArray(studioTypes) ? studioTypes : [];
    const hasMusik = types.includes("Musik");
    const hasFotoOrVideo = types.includes("Foto") || types.includes("Video");

    let allowed;
    if (hasMusik && !hasFotoOrVideo) {
        allowed = new Set(MUSIK_OPTIONAL_CATEGORIES);
    } else if (hasMusik && hasFotoOrVideo) {
        allowed = new Set([...PHOTO_VIDEO_OPTIONAL_CATEGORIES, ...MUSIK_OPTIONAL_CATEGORIES]);
    } else {
        allowed = new Set(PHOTO_VIDEO_OPTIONAL_CATEGORIES);
    }

    return EQUIPMENT_OPTIONAL_CATEGORIES.filter((category) => allowed.has(category));
};

const stroke = {
    stroke: "#A1A7AE",
    strokeWidth: 2.80697,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const IconSonstiges = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M35.0078 13.3325C35.0072 12.748 34.8529 12.1739 34.5604 11.6678C34.2678 11.1617 33.8474 10.7415 33.3411 10.4492L21.6745 3.78252C21.1677 3.48996 20.5929 3.33594 20.0078 3.33594C19.4227 3.33594 18.8479 3.48996 18.3411 3.78252L6.67448 10.4492C6.16825 10.7415 5.74778 11.1617 5.45525 11.6678C5.16272 12.1739 5.00841 12.748 5.00781 13.3325V26.6659C5.00841 27.2504 5.16272 27.8245 5.45525 28.3306C5.74778 28.8367 6.16825 29.2569 6.67448 29.5492L18.3411 36.2159C18.8479 36.5084 19.4227 36.6624 20.0078 36.6624C20.5929 36.6624 21.1677 36.5084 21.6745 36.2159L33.3411 29.5492C33.8474 29.2569 34.2678 28.8367 34.5604 28.3306C34.8529 27.8245 35.0072 27.2504 35.0078 26.6659V13.3325Z"
            {...stroke}
        />
        <path d="M5.5 11.668L20 20.0013L34.5 11.668" {...stroke} />
        <path d="M20.0078 36.6667V20" {...stroke} />
    </svg>
);

const IconAufnahmeMikrofone = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M18.3353 12.668L8.34527 26.318C8.11021 26.6391 7.9978 27.0337 8.02836 27.4305C8.05893 27.8272 8.23046 28.2 8.51194 28.4813L9.87361 29.8446C10.1592 30.1299 10.5386 30.3018 10.9414 30.3285C11.3441 30.3552 11.7429 30.2348 12.0636 29.9896L25.1519 19.9996"
            {...stroke}
        />
        <path
            d="M27.499 35.2913C25.8323 34.168 23.9523 33.3346 21.6657 33.3346C18.2357 33.3346 15.119 37.2613 11.6657 36.668C8.21234 36.0746 7.04067 31.053 9.16567 29.168"
            {...stroke}
        />
        <path
            d="M26.6693 19.9987C31.2716 19.9987 35.0026 16.2677 35.0026 11.6654C35.0026 7.06299 31.2716 3.33203 26.6693 3.33203C22.0669 3.33203 18.3359 7.06299 18.3359 11.6654C18.3359 16.2677 22.0669 19.9987 26.6693 19.9987Z"
            {...stroke}
        />
    </svg>
);

const IconLautsprecher = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M5 23.3294H10C10.8841 23.3294 11.7319 23.6806 12.357 24.3057C12.9821 24.9309 13.3333 25.7787 13.3333 26.6628V31.6628C13.3333 32.5468 12.9821 33.3947 12.357 34.0198C11.7319 34.6449 10.8841 34.9961 10 34.9961H8.33333C7.44928 34.9961 6.60143 34.6449 5.97631 34.0198C5.35119 33.3947 5 32.5468 5 31.6628V19.9961C5 16.0178 6.58035 12.2025 9.3934 9.38949C12.2064 6.57645 16.0218 4.99609 20 4.99609C23.9782 4.99609 27.7936 6.57645 30.6066 9.38949C33.4196 12.2025 35 16.0178 35 19.9961V31.6628C35 32.5468 34.6488 33.3947 34.0237 34.0198C33.3986 34.6449 32.5507 34.9961 31.6667 34.9961H30C29.1159 34.9961 28.2681 34.6449 27.643 34.0198C27.0179 33.3947 26.6667 32.5468 26.6667 31.6628V26.6628C26.6667 25.7787 27.0179 24.9309 27.643 24.3057C28.2681 23.6806 29.1159 23.3294 30 23.3294H35"
            {...stroke}
        />
    </svg>
);

const IconRecorderSoftware = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M25.0524 15.7315C25.2161 15.8262 25.352 15.9623 25.4464 16.1261C25.5409 16.29 25.5906 16.4757 25.5906 16.6648C25.5906 16.854 25.5409 17.0397 25.4464 17.2036C25.352 17.3674 25.2161 17.5035 25.0524 17.5982L18.2774 21.5182C18.1138 21.6128 17.9281 21.6627 17.7391 21.6627C17.5501 21.6627 17.3644 21.6128 17.2008 21.5182C17.0371 21.4235 16.9014 21.2874 16.8071 21.1235C16.7129 20.9597 16.6636 20.7739 16.6641 20.5848V12.7448C16.6637 12.5561 16.7131 12.3705 16.8073 12.2069C16.9014 12.0433 17.037 11.9073 17.2003 11.8127C17.3637 11.7181 17.5491 11.6682 17.7379 11.668C17.9267 11.6678 18.1122 11.7173 18.2757 11.8115L25.0524 15.7315Z"
            {...stroke}
        />
        <path d="M20 28.332V34.9987" {...stroke} />
        <path d="M13.3359 35H26.6693" {...stroke} />
        <path
            d="M33.3359 5H6.66927C4.82832 5 3.33594 6.49238 3.33594 8.33333V25C3.33594 26.841 4.82832 28.3333 6.66927 28.3333H33.3359C35.1769 28.3333 36.6693 26.841 36.6693 25V8.33333C36.6693 6.49238 35.1769 5 33.3359 5Z"
            {...stroke}
        />
    </svg>
);

const IconMischpulte = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path d="M16.6667 8.33594H5" {...stroke} />
        <path d="M20 31.668H5" {...stroke} />
        <path d="M23.3359 5V11.6667" {...stroke} />
        <path d="M26.6641 28.3359V35.0026" {...stroke} />
        <path d="M35 20H20" {...stroke} />
        <path d="M34.9974 31.668H26.6641" {...stroke} />
        <path d="M35.0026 8.33594H23.3359" {...stroke} />
        <path d="M13.3359 16.668V23.3346" {...stroke} />
        <path d="M13.3333 20H5" {...stroke} />
    </svg>
);

const IconKlangbearbeitung = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M3.33594 21.6693C4.21999 21.6693 5.06784 21.3181 5.69296 20.693C6.31808 20.0678 6.66927 19.22 6.66927 18.3359V11.6693C6.66927 10.7852 7.02046 9.93737 7.64558 9.31225C8.2707 8.68713 9.11855 8.33594 10.0026 8.33594C10.8867 8.33594 11.7345 8.68713 12.3596 9.31225C12.9847 9.93737 13.3359 10.7852 13.3359 11.6693V33.3359C13.3359 34.22 13.6871 35.0678 14.3122 35.693C14.9374 36.3181 15.7852 36.6693 16.6693 36.6693C17.5533 36.6693 18.4012 36.3181 19.0263 35.693C19.6514 35.0678 20.0026 34.22 20.0026 33.3359V6.66927C20.0026 5.78522 20.3538 4.93737 20.9789 4.31225C21.604 3.68713 22.4519 3.33594 23.3359 3.33594C24.22 3.33594 25.0678 3.68713 25.693 4.31225C26.3181 4.93737 26.6693 5.78522 26.6693 6.66927V28.3359C26.6693 29.22 27.0205 30.0678 27.6456 30.693C28.2707 31.3181 29.1185 31.6693 30.0026 31.6693C30.8867 31.6693 31.7345 31.3181 32.3596 30.693C32.9847 30.0678 33.3359 29.22 33.3359 28.3359V21.6693C33.3359 20.7852 33.6871 19.9374 34.3122 19.3122C34.9374 18.6871 35.7852 18.3359 36.6693 18.3359"
            {...stroke}
        />
    </svg>
);

const IconInstrumente = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path d="M19.8281 20.164L27.3515 12.6406" {...stroke} />
        <path
            d="M33.5027 3.8319C33.1912 3.52652 32.7723 3.35547 32.336 3.35547C31.8998 3.35547 31.4809 3.52652 31.1694 3.8319L29.3127 5.68856C28.6875 6.31354 28.3362 7.16125 28.336 8.04523V10.2852C28.3359 11.1692 27.9845 12.0169 27.3594 12.6419C27.9844 12.0167 28.8321 11.6654 29.716 11.6652H31.956C32.84 11.665 33.6877 11.3137 34.3127 10.6886L36.1694 8.8319C36.4748 8.52035 36.6458 8.10149 36.6458 7.66523C36.6458 7.22898 36.4748 6.81011 36.1694 6.49856L33.5027 3.8319Z"
            {...stroke}
        />
        <path d="M10 26.6641L13.3333 29.9974" {...stroke} />
        <path
            d="M13.7193 16.4154C14.098 15.5028 14.7387 14.7229 15.5604 14.1741C16.382 13.6253 17.3479 13.3323 18.3359 13.332C20.5461 13.332 22.6657 14.21 24.2285 15.7728C25.7913 17.3356 26.6693 19.4552 26.6693 21.6654C26.669 22.6534 26.376 23.6193 25.8272 24.4409C25.2784 25.2626 24.4985 25.9033 23.5859 26.282L22.0526 26.9154C21.4444 27.1691 20.925 27.5974 20.5601 28.1462C20.1952 28.695 20.0012 29.3397 20.0026 29.9987C20.0026 31.7668 19.3002 33.4625 18.05 34.7127C16.7997 35.963 15.104 36.6654 13.3359 36.6654C10.6838 36.6654 8.14023 35.6118 6.26487 33.7364C4.38951 31.8611 3.33594 29.3175 3.33594 26.6654C3.33594 24.8973 4.03832 23.2016 5.28856 21.9513C6.5388 20.7011 8.23449 19.9987 10.0026 19.9987C10.6616 20.0001 11.3063 19.8061 11.8551 19.4412C12.4039 19.0763 12.8322 18.5569 13.0859 17.9487L13.7193 16.4154Z"
            {...stroke}
        />
    </svg>
);

const IconAkustikKabel = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M28.3307 31.6693C27.8887 31.6693 27.4648 31.4937 27.1522 31.1811C26.8397 30.8686 26.6641 30.4446 26.6641 30.0026V26.6693C26.6641 25.7852 27.0153 24.9374 27.6404 24.3122C28.2655 23.6871 29.1133 23.3359 29.9974 23.3359H33.3307C34.2148 23.3359 35.0626 23.6871 35.6878 24.3122C36.3129 24.9374 36.6641 25.7852 36.6641 26.6693V30.0026C36.6641 30.4446 36.4885 30.8686 36.1759 31.1811C35.8633 31.4937 35.4394 31.6693 34.9974 31.6693H28.3307Z"
            {...stroke}
        />
        <path d="M28.3359 35.0013V31.668" {...stroke} />
        <path
            d="M31.6693 23.3333V10.8333C31.6693 9.28624 31.0547 7.80251 29.9607 6.70854C28.8668 5.61458 27.383 5 25.8359 5C24.2888 5 22.8051 5.61458 21.7111 6.70854C20.6172 7.80251 20.0026 9.28624 20.0026 10.8333V29.1667C20.0026 30.7138 19.388 32.1975 18.2941 33.2915C17.2001 34.3854 15.7164 35 14.1693 35C12.6222 35 11.1384 34.3854 10.0445 33.2915C8.95052 32.1975 8.33594 30.7138 8.33594 29.1667V16.6667"
            {...stroke}
        />
        <path d="M35 35.0013V31.668" {...stroke} />
        <path d="M5 8.33333V5" {...stroke} />
        <path
            d="M6.66927 16.6693C5.78522 16.6693 4.93737 16.3181 4.31225 15.693C3.68713 15.0678 3.33594 14.22 3.33594 13.3359V10.0026C3.33594 9.56058 3.51153 9.13665 3.82409 8.82409C4.13665 8.51153 4.56058 8.33594 5.0026 8.33594H11.6693C12.1113 8.33594 12.5352 8.51153 12.8478 8.82409C13.1603 9.13665 13.3359 9.56058 13.3359 10.0026V13.3359C13.3359 14.22 12.9847 15.0678 12.3596 15.693C11.7345 16.3181 10.8867 16.6693 10.0026 16.6693H6.66927Z"
            {...stroke}
        />
        <path d="M11.6641 8.33333V5" {...stroke} />
    </svg>
);

const IconLichtequipment = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path d="M25.5 32.6055L26.675 36.6688" {...stroke} />
        <path d="M28.3359 26.668L34.5993 30.1646" {...stroke} />
        <path d="M31.6719 20.8287L36.7152 19.832" {...stroke} />
        <path
            d="M12.6917 10.5014C11.5325 9.95707 10.2085 9.87838 8.9929 10.2815C7.7773 10.6846 6.76273 11.5389 6.15842 12.668L3.85842 17.318C3.31913 18.4755 3.24312 19.7955 3.646 21.0072C4.04887 22.2189 4.90009 23.2306 6.02508 23.8347L17.5084 29.8297C17.7042 29.9277 17.9173 29.9861 18.1357 30.0017C18.3541 30.0173 18.5734 29.9897 18.7811 29.9206C18.9888 29.8514 19.1808 29.7419 19.3462 29.5985C19.5116 29.4551 19.6472 29.2805 19.7451 29.0847L24.9217 18.733C25.1191 18.3378 25.1515 17.8804 25.0118 17.4613C24.8721 17.0423 24.5718 16.6958 24.1767 16.498L12.6917 10.5014Z"
            {...stroke}
        />
        <path d="M13.3359 14.9987V3.33203" {...stroke} />
    </svg>
);

const IconKameras = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M26.6719 21.6648L35.3769 27.4682C35.5024 27.5517 35.6482 27.5996 35.7987 27.6068C35.9493 27.614 36.099 27.5802 36.2319 27.5091C36.3648 27.4379 36.4759 27.3321 36.5534 27.2028C36.6308 27.0735 36.6718 26.9256 36.6719 26.7748V13.1148C36.6719 12.9682 36.6333 12.8242 36.5598 12.6972C36.4864 12.5703 36.3808 12.465 36.2536 12.392C36.1265 12.319 35.9823 12.2808 35.8357 12.2813C35.6891 12.2818 35.5452 12.3209 35.4185 12.3948L26.6719 17.4982"
            {...stroke}
        />
        <path
            d="M23.3359 10.0039H6.66927C4.82832 10.0039 3.33594 11.4963 3.33594 13.3372V26.6706C3.33594 28.5115 4.82832 30.0039 6.66927 30.0039H23.3359C25.1769 30.0039 26.6693 28.5115 26.6693 26.6706V13.3372C26.6693 11.4963 25.1769 10.0039 23.3359 10.0039Z"
            {...stroke}
        />
    </svg>
);

const IconStative = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path d="M14.5859 17.8924L7.08594 30.8828" {...stroke} />
        <path d="M25.4141 17.8924L32.9141 30.8828" {...stroke} />
        <path d="M20 3.33594L20 36.6641M16 3.33594L24 3.33594" {...stroke} />
    </svg>
);

const IconSetAccessoires = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
        <path
            d="M33.3385 15.0013V10.0013C33.3385 9.11725 32.9874 8.2694 32.3622 7.64428C31.7371 7.01916 30.8893 6.66797 30.0052 6.66797H10.0052C9.12115 6.66797 8.27331 7.01916 7.64819 7.64428C7.02306 8.2694 6.67188 9.11725 6.67188 10.0013V15.0013"
            {...stroke}
        />
        <path
            d="M3.33594 26.6706C3.33594 27.5546 3.68713 28.4025 4.31225 29.0276C4.93737 29.6527 5.78522 30.0039 6.66927 30.0039H33.3359C34.22 30.0039 35.0678 29.6527 35.693 29.0276C36.3181 28.4025 36.6693 27.5546 36.6693 26.6706V18.3372C36.6693 17.4532 36.3181 16.6053 35.693 15.9802C35.0678 15.3551 34.22 15.0039 33.3359 15.0039C32.4519 15.0039 31.604 15.3551 30.9789 15.9802C30.3538 16.6053 30.0026 17.4532 30.0026 18.3372V20.8372C30.0026 21.0583 29.9148 21.2702 29.7585 21.4265C29.6022 21.5828 29.3903 21.6706 29.1693 21.6706H10.8359C10.6149 21.6706 10.403 21.5828 10.2467 21.4265C10.0904 21.2702 10.0026 21.0583 10.0026 20.8372V18.3372C10.0026 17.4532 9.65141 16.6053 9.02629 15.9802C8.40117 15.3551 7.55333 15.0039 6.66927 15.0039C5.78522 15.0039 4.93737 15.3551 4.31225 15.9802C3.68713 16.6053 3.33594 17.4532 3.33594 18.3372V26.6706Z"
            {...stroke}
        />
        <path d="M6.67188 30.0039V33.3372" {...stroke} />
        <path d="M33.3359 30.0039V33.3372" {...stroke} />
        <path d="M20.0078 6.66797V21.668" {...stroke} />
    </svg>
);

const CATEGORY_DEFAULT_SVG = {
    Sonstiges: IconSonstiges,
    "Aufnahme & Mikrofone": IconAufnahmeMikrofone,
    "Lautsprecher & Kopfhörer (Monitoring)": IconLautsprecher,
    "Recorder, Software & Computer": IconRecorderSoftware,
    "Mischpulte & Steuerung": IconMischpulte,
    "Klangbearbeitung (Preamps & Effekte)": IconKlangbearbeitung,
    Instrumente: IconInstrumente,
    "Akustik & Kabel": IconAkustikKabel,
    Lichtequipment: IconLichtequipment,
    "Kameras und Objektive": IconKameras,
    Stative: IconStative,
    "Set-Accessoires": IconSetAccessoires,
};

/**
 * Renders the category illustration used when there is no uploaded photo.
 * Persisted items keep `image: null`; `category` selects which illustration to show.
 */
export const CategoryDefaultIcon = ({ category }) => {
    const Icon = CATEGORY_DEFAULT_SVG[category] || IconSonstiges;
    return <Icon />;
};
